import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { fabric } from 'fabric';
import { jsPDF } from 'jspdf';
import { useNavigate, useParams } from 'react-router-dom';
import apiService from '../services/ApiService';
import resolveHubBaseUrl from '../utils/hubBaseUrl';
import aulaDocenteService, {
  aulaDocenteArchivosService,
  aulaDocenteLinksService,
  aulaDocenteMensajesService
} from '../services/AulaDocenteService';
import { useAuth } from '../contexts/AuthContext';
import './AulaDocenteAccessPage.css';

const BOARD_MIN_ZOOM = 0.3;
const BOARD_MAX_ZOOM = 3.5;
const BOARD_OBJECT_JSON_PROPS = [
  'src',
  'left',
  'top',
  'scaleX',
  'scaleY',
  'angle',
  'width',
  'height',
  'text',
  'fontSize',
  'fontFamily',
  'fontWeight',
  'fontStyle',
  'underline',
  'fill',
  'textAlign',
  'charSpacing',
  'lineHeight',
  'editable'
];

const isBoardEditableObject = (object) => ['image', 'i-text', 'textbox', 'text'].includes(object?.type);

const isBoardTextObject = (object) => ['i-text', 'textbox', 'text'].includes(object?.type);

const getBoardObjectJson = (object) => object.toJSON(BOARD_OBJECT_JSON_PROPS);

const getBoardSelectionObjects = (object) => {
  if (!object) return [];
  if (object.type === 'activeSelection' && typeof object.getObjects === 'function') {
    return object.getObjects();
  }
  return [object];
};

const applyBoardObjectInteractivity = (object, canEdit) => {
  const editableObject = isBoardEditableObject(object);
  object.selectable = Boolean(canEdit && editableObject);
  object.evented = Boolean(canEdit && editableObject);
  if ('editable' in object) {
    object.editable = Boolean(canEdit && editableObject);
  }
};

const getId = (item) => item?.id || item?.Id || '';

const getAulaNombre = (aula) => aula?.nombre || aula?.Nombre || 'Aula docente';

const formatDateTime = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const escapeHtml = (value) => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const buildMessageHtml = (value) => `<p>${escapeHtml(value).replace(/\n/g, '<br />')}</p>`;

const normalizeChatExportText = (value) => (
  String(value ?? '')
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '\n')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
);

const getHubBaseUrl = () => (
  resolveHubBaseUrl()
);

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const AulaDocenteAccessPage = () => {
  const { aulaId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const sessionIdRef = useRef(`aula-docente-${Date.now()}-${Math.random().toString(16).slice(2)}`);
  const chatConnectionRef = useRef(null);
  const chatPresenceConnectionRef = useRef(null);
  const chatScrollRef = useRef(null);
  const chatInputRef = useRef(null);
  const boardConnectionRef = useRef(null);
  const boardPresenceConnectionRef = useRef(null);
  const boardCanvasRef = useRef(null);
  const boardContainerRef = useRef(null);
  const fabricInstanceRef = useRef(null);
  const imageInputRef = useRef(null);
  const isPanningRef = useRef(false);
  const boardPanModeRef = useRef(false);
  const boardSelectModeRef = useRef(false);
  const boardNavigationRef = useRef({
    mode: null,
    lastClientX: null,
    lastClientY: null,
    lastDistance: 0
  });
  const boardZoomRef = useRef(1);
  const myBoardNameRef = useRef('');
  const callConnectionRef = useRef(null);

  const [detail, setDetail] = useState(null);
  const [archivos, setArchivos] = useState([]);
  const [links, setLinks] = useState([]);
  const [mensajes, setMensajes] = useState([]);
  const [mensajesTotal, setMensajesTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [error, setError] = useState('');
  const [messageTitle, setMessageTitle] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [messageSaving, setMessageSaving] = useState(false);
  const [replyParent, setReplyParent] = useState(null);
  const [replyBody, setReplyBody] = useState('');
  const [replySaving, setReplySaving] = useState(false);
  const [expandedMessages, setExpandedMessages] = useState(new Set());
  const [resourceError, setResourceError] = useState('');
  const [chatOpen, setChatOpen] = useState(false);
  const [chatConnecting, setChatConnecting] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatUsers, setChatUsers] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [boardOpen, setBoardOpen] = useState(false);
  const [boardConnecting, setBoardConnecting] = useState(false);
  const [boardUsers, setBoardUsers] = useState([]);
  const [boardColor, setBoardColor] = useState('#0f172a');
  const [boardWidth, setBoardWidth] = useState(3);
  const [boardEraserWidth, setBoardEraserWidth] = useState(10);
  const [boardTextSize, setBoardTextSize] = useState(28);
  const [boardStartedBy, setBoardStartedBy] = useState('');
  const [boardStatus, setBoardStatus] = useState('Nadie inició la pizarra aún.');
  const [boardEraser, setBoardEraser] = useState(false);
  const [boardExporting, setBoardExporting] = useState(false);
  const [boardPanMode, setBoardPanMode] = useState(false);
  const [boardSelectMode, setBoardSelectMode] = useState(false);
  const [boardZoom, setBoardZoom] = useState(1);
  const [callInfo, setCallInfo] = useState(null);
  const [callConnecting, setCallConnecting] = useState(false);

  const aula = detail?.aula || null;
  const features = detail?.features || {};
  const asistentes = Array.isArray(detail?.asistentes) ? detail.asistentes : [];
  const aulaNombre = getAulaNombre(aula);
  const canUseMessages = features.permiteEnviarMensajesEnAula !== false && features.permiteForoEnAula !== false;
  const canUseFiles = features.permiteSubirArchivosAlAula !== false;
  const canUseLinks = features.permiteLinks !== false && features.permiteLinksEnAula !== false;
  const canUseChat = features.permiteChatGrupalEnAula !== false;
  const canUseBoard = features.permitePizarraCompartidaEnAula !== false;
  const canUseVideo = features.permiteIniciarVideoLlamadaEnAula !== false;
  const canUseAssistants = features.permiteAgentesClasicosGPT !== false;
  const canCreateForumThreads = Boolean(detail?.esPropietario || detail?.permisos?.puedeAdministrarRecursos);
  const jitsiBase = process.env.REACT_APP_JITSI_BASE || 'https://meet.jit.si';

  const userLabel = useMemo(
    () => user?.displayName || user?.name || user?.email || 'Usuario',
    [user]
  );

  const normalizeRealtimeUsers = useCallback((users) => Array.from(new Set(
    (Array.isArray(users) ? users : [])
      .filter((name) => typeof name === 'string' && name.trim() && !name.startsWith('__observer__'))
      .map((name) => name.trim())
  )), []);

  const registerTelemetry = useCallback(async (tipoEvento, payload = {}) => {
    if (!aulaId) return;
    try {
      await aulaDocenteService.registrarAulaDocenteTelemetry(aulaId, {
        tipoEvento,
        sessionId: sessionIdRef.current,
        origen: 'aula_docente_ingreso',
        ...payload
      });
    } catch (err) {
      console.warn('No pudimos registrar telemetría del aula docente', err);
    }
  }, [aulaId]);

  const loadResources = useCallback(async () => {
    if (!aulaId) return;

    setResourcesLoading(true);
    setResourceError('');
    try {
      const requests = [
        canUseFiles
          ? aulaDocenteArchivosService.list(aulaId).catch((err) => ({ error: err, items: [] }))
          : Promise.resolve({ items: [] }),
        canUseLinks
          ? aulaDocenteLinksService.list(aulaId).catch((err) => ({ error: err, items: [] }))
          : Promise.resolve({ items: [] }),
        canUseMessages
          ? aulaDocenteMensajesService.getMensajes(aulaId, { start: 0, end: 20, includeTotal: true }).catch((err) => ({ error: err, items: [] }))
          : Promise.resolve({ items: [] })
      ];

      const [archivosResponse, linksResponse, mensajesResponse] = await Promise.all(requests);
      const fileItems = archivosResponse?.items || archivosResponse?.Items || [];
      const linkItems = linksResponse?.items || linksResponse?.Items || [];
      const messageItems = mensajesResponse?.items || mensajesResponse?.Items || [];

      setArchivos(Array.isArray(fileItems) ? fileItems : []);
      setLinks(Array.isArray(linkItems) ? linkItems : []);
      setMensajes(Array.isArray(messageItems) ? messageItems : []);
      setMensajesTotal(mensajesResponse?.total ?? mensajesResponse?.Total ?? (Array.isArray(messageItems) ? messageItems.length : 0));

      const firstError = archivosResponse?.error || linksResponse?.error || mensajesResponse?.error;
      if (firstError) {
        setResourceError(firstError.message || 'Algunos recursos no pudieron cargarse.');
      }
    } finally {
      setResourcesLoading(false);
    }
  }, [aulaId, canUseFiles, canUseLinks, canUseMessages]);

  useEffect(() => {
    if (!aulaId) return;

    let disposed = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await aulaDocenteService.getAulaDocenteAccessDetalle(aulaId);
        if (disposed) return;
        setDetail(response);
        await registerTelemetry('aula_docente.page_view', {
          aulaNombre: getAulaNombre(response?.aula)
        });
      } catch (err) {
        if (!disposed) {
          setError(err.message || 'No pudimos cargar el aula docente.');
        }
      } finally {
        if (!disposed) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      disposed = true;
    };
  }, [aulaId, registerTelemetry]);

  useEffect(() => {
    if (!detail || !aulaId) return;
    void loadResources();
  }, [detail, aulaId, loadResources]);

  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [chatMessages]);

  useEffect(() => () => {
    chatConnectionRef.current?.stop().catch(() => {});
    chatPresenceConnectionRef.current?.stop().catch(() => {});
    boardConnectionRef.current?.stop().catch(() => {});
    boardPresenceConnectionRef.current?.stop().catch(() => {});
    callConnectionRef.current?.stop().catch(() => {});
    const instance = fabricInstanceRef.current;
    if (instance) {
      if (typeof instance.__boardTouchCleanup === 'function') {
        instance.__boardTouchCleanup();
      }
      instance.dispose();
      fabricInstanceRef.current = null;
    }
  }, []);

  const setupCallHub = useCallback(async () => {
    if (!aulaId || !canUseVideo || callConnectionRef.current) return;

    setCallConnecting(true);
    try {
      const connection = new HubConnectionBuilder()
        .withUrl(`${getHubBaseUrl()}/hubs/aula-docente-call`, {
          withCredentials: true,
          accessTokenFactory: () => apiService.getTokenFromCookies() || ''
        })
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Warning)
        .build();

      connection.on('VideoCallStarted', (payload) => {
        if (!payload) return;
        setCallInfo({
          startedBy: payload.startedBy || 'Docente',
          roomUrl: payload.roomUrl,
          startedAt: payload.startedAt,
          expiresAt: payload.expiresAt
        });
      });
      connection.on('VideoCallEnded', () => {
        setCallInfo(null);
      });
      connection.onreconnected(() => {
        connection.invoke('JoinAulaDocenteCall', aulaId).catch(() => {});
      });

      await connection.start();
      await connection.invoke('JoinAulaDocenteCall', aulaId);
      callConnectionRef.current = connection;
    } catch (err) {
      setResourceError(err.message || 'No pudimos conectar la videollamada docente.');
    } finally {
      setCallConnecting(false);
    }
  }, [aulaId, canUseVideo]);

  useEffect(() => {
    if (!detail || !aulaId || !canUseVideo) return;
    void setupCallHub();
  }, [detail, aulaId, canUseVideo, setupCallHub]);

  const setupChatPresenceHub = useCallback(async () => {
    if (!aulaId || !canUseChat || chatPresenceConnectionRef.current) return;

    try {
      const connection = new HubConnectionBuilder()
        .withUrl(`${getHubBaseUrl()}/hubs/aula-docente-chat`, {
          withCredentials: true,
          accessTokenFactory: () => apiService.getTokenFromCookies() || ''
        })
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Warning)
        .build();

      const updateChatUsers = (users) => {
        setChatUsers(normalizeRealtimeUsers(users));
      };

      connection.on('ConnectedUsers', updateChatUsers);
      connection.on('ChatSessionStarted', (payload) => {
        const users = normalizeRealtimeUsers(payload?.connectedUsers);
        if (users.length > 0) {
          setChatUsers(users);
        } else if (payload?.startedBy) {
          setChatUsers((prev) => {
            const list = normalizeRealtimeUsers(prev);
            return list.includes(payload.startedBy) ? list : [...list, payload.startedBy];
          });
        }
      });
      connection.on('ChatSessionEnded', () => {
        setChatUsers([]);
      });
      connection.on('UserJoined', (name) => {
        if (!name) return;
        setChatUsers((prev) => {
          const list = normalizeRealtimeUsers(prev);
          return list.includes(name) ? list : [...list, name];
        });
      });
      connection.on('UserLeft', (name) => {
        setChatUsers((prev) => normalizeRealtimeUsers(prev).filter((item) => item !== name));
      });
      connection.onreconnected(() => {
        connection.invoke('WatchAulaDocenteChat', aulaId).catch(() => {});
      });

      await connection.start();
      await connection.invoke('WatchAulaDocenteChat', aulaId);
      chatPresenceConnectionRef.current = connection;
    } catch (err) {
      console.warn('No pudimos conectar la presencia del chat docente', err);
    }
  }, [aulaId, canUseChat, normalizeRealtimeUsers]);

  const setupBoardPresenceHub = useCallback(async () => {
    if (!aulaId || !canUseBoard || boardPresenceConnectionRef.current) return;

    try {
      const connection = new HubConnectionBuilder()
        .withUrl(`${getHubBaseUrl()}/hubs/aula-docente-board`, {
          withCredentials: true,
          accessTokenFactory: () => apiService.getTokenFromCookies() || ''
        })
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Warning)
        .build();

      const updateBoardPresence = (users) => {
        const cleaned = normalizeRealtimeUsers(users);
        setBoardUsers(cleaned);
        if (cleaned.length > 0) {
          setBoardStartedBy((prev) => prev || cleaned[0]);
          setBoardStatus(`${cleaned[0]} inició una pizarra colaborativa (en curso).`);
        } else if (!boardOpen) {
          setBoardStartedBy('');
          setBoardStatus('Nadie inició la pizarra aún.');
        }
      };

      connection.on('ConnectedUsersBoard', updateBoardPresence);
      connection.on('BoardSessionStarted', (payload) => {
        const users = normalizeRealtimeUsers(payload?.connectedUsers);
        const startedBy = payload?.startedBy || users[0] || 'Docente';
        setBoardUsers(users.length > 0 ? users : [startedBy]);
        setBoardStartedBy(startedBy);
        setBoardStatus(`${startedBy} inició una pizarra colaborativa (en curso).`);
      });
      connection.on('BoardSessionEnded', () => {
        setBoardUsers([]);
        setBoardStartedBy('');
        if (!boardOpen) {
          setBoardStatus('Nadie inició la pizarra aún.');
        }
      });
      connection.on('UserJoinedBoard', (name) => {
        if (!name || name.startsWith('__observer__')) return;
        setBoardUsers((prev) => {
          const list = normalizeRealtimeUsers(prev);
          return list.includes(name) ? list : [...list, name];
        });
        setBoardStartedBy((prev) => prev || name);
        setBoardStatus(`${name} está en la pizarra docente.`);
      });
      connection.on('UserLeftBoard', (name) => {
        setBoardUsers((prev) => {
          const next = normalizeRealtimeUsers(prev).filter((item) => item !== name);
          if (next.length === 0 && !boardOpen) {
            setBoardStartedBy('');
            setBoardStatus('Nadie inició la pizarra aún.');
          }
          return next;
        });
      });
      connection.onreconnected(() => {
        connection.invoke('WatchBoard', aulaId).catch(() => {});
      });

      await connection.start();
      await connection.invoke('WatchBoard', aulaId);
      boardPresenceConnectionRef.current = connection;
    } catch (err) {
      console.warn('No pudimos conectar la presencia de pizarra docente', err);
    }
  }, [aulaId, boardOpen, canUseBoard, normalizeRealtimeUsers]);

  useEffect(() => {
    if (!detail || !aulaId) return;
    if (canUseChat) void setupChatPresenceHub();
    if (canUseBoard) void setupBoardPresenceHub();
  }, [detail, aulaId, canUseChat, canUseBoard, setupChatPresenceHub, setupBoardPresenceHub]);

  const startChatConnection = async () => {
    if (!aulaId || !canUseChat || chatConnectionRef.current) return;

    setChatConnecting(true);
    setResourceError('');
    try {
      const connection = new HubConnectionBuilder()
        .withUrl(`${getHubBaseUrl()}/hubs/aula-docente-chat`, {
          withCredentials: true,
          accessTokenFactory: () => apiService.getTokenFromCookies() || ''
        })
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Warning)
        .build();

      connection.on('ReceiveMessage', (payload) => {
        setChatMessages((prev) => [...prev, { ...payload, tipo: 'mensaje' }]);
      });
      connection.on('UserJoined', (name, fechaUtc) => {
        setChatMessages((prev) => [...prev, { autor: 'sistema', contenido: `${name} se sumó al chat`, fechaUtc, tipo: 'sistema' }]);
        setChatUsers((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          return list.includes(name) ? list : [...list, name];
        });
      });
      connection.on('UserLeft', (name, fechaUtc) => {
        setChatMessages((prev) => [...prev, { autor: 'sistema', contenido: `${name} salió del chat`, fechaUtc, tipo: 'sistema' }]);
        setChatUsers((prev) => (Array.isArray(prev) ? prev.filter((item) => item !== name) : []));
      });
      connection.on('ConnectedUsers', (users) => {
        setChatUsers(normalizeRealtimeUsers(users));
      });
      connection.on('ChatSessionEnded', () => {
        setChatUsers([]);
      });
      connection.onreconnected(() => {
        connection.invoke('JoinAulaDocente', aulaId, userLabel).catch(() => {});
      });

      await connection.start();
      await connection.invoke('JoinAulaDocente', aulaId, userLabel);
      chatConnectionRef.current = connection;
      setChatOpen(true);
      setChatUsers((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        return list.includes(userLabel) ? list : [...list, userLabel];
      });
      const wasChatAlreadyActive = chatUsers.length > 0;
      if (!wasChatAlreadyActive) {
        await registerTelemetry('aula_docente.chat_started', {
          origen: 'chat_grupal',
          displayName: userLabel
        });
      }
      await registerTelemetry('aula_docente.chat_joined', {
        origen: 'chat_grupal',
        displayName: userLabel
      });
    } catch (err) {
      setResourceError(err.message || 'No pudimos conectar el chat docente.');
    } finally {
      setChatConnecting(false);
    }
  };

  const handleSendChatMessage = async () => {
    const content = chatInput.trim();
    if (!content || !chatConnectionRef.current || !aulaId) return;

    setChatInput('');
    if (chatInputRef.current) {
      chatInputRef.current.style.height = '40px';
    }
    try {
      await chatConnectionRef.current.invoke('SendMessage', aulaId, userLabel, content);
    } catch (err) {
      setResourceError(err.message || 'No pudimos enviar el mensaje.');
      setChatInput(content);
    }
  };

  const handleChatInputChange = (event) => {
    setChatInput(event.target.value);
    if (chatInputRef.current) {
      chatInputRef.current.style.height = 'auto';
      chatInputRef.current.style.height = `${Math.min(chatInputRef.current.scrollHeight, 140)}px`;
    }
  };

  const downloadChatConversation = () => {
    const lines = chatMessages.map((message) => {
      const who = message.autor === 'sistema' ? '[sistema]' : (message.autor || 'Docente');
      const when = formatDateTime(message.fechaUtc || Date.now());
      return `${when} - ${who}: ${normalizeChatExportText(message.contenido)}`;
    });
    const blob = new Blob([lines.join('\n').replace(/\n/g, '\r\n')], { type: 'text/plain;charset=utf-8' });
    downloadBlob(blob, `chat-aula-docente-${aulaId || 'aula'}.txt`);
  };

  const leaveChat = async () => {
    if (chatConnectionRef.current) {
      try {
        await chatConnectionRef.current.stop();
      } catch (_) {}
      chatConnectionRef.current = null;
    }

    setChatOpen(false);
    setChatMessages([]);
  };

  const sendBoardAction = async (action) => {
    if (!boardConnectionRef.current || !aulaId) return;
    try {
      await boardConnectionRef.current.invoke('SendBoardAction', aulaId, userLabel, JSON.stringify({ ...action, sourceId: sessionIdRef.current }));
    } catch (err) {
      setResourceError(err.message || 'No pudimos sincronizar la pizarra.');
    }
  };

  const handleBoardColorChange = (event) => {
    const nextColor = event.target.value;
    setBoardColor(nextColor);

    const instance = fabricInstanceRef.current;
    if (!instance) return;

    const selectedTextObjects = getBoardSelectionObjects(instance.getActiveObject()).filter(isBoardTextObject);
    if (selectedTextObjects.length === 0) return;

    selectedTextObjects.forEach((object) => {
      object.set('fill', nextColor);
      sendBoardAction({ type: 'fabric-path', object: getBoardObjectJson(object) });
    });
    instance.requestRenderAll();

    const snapshot = instance.toJSON(BOARD_OBJECT_JSON_PROPS);
    snapshot.background = instance.backgroundColor || '#ffffff';
    sendBoardAction({ type: 'snapshot', snapshot });
  };

  const handleBoardTextSizeChange = (event) => {
    const nextSize = Number(event.target.value);
    setBoardTextSize(nextSize);

    const instance = fabricInstanceRef.current;
    if (!instance) return;

    const selectedTextObjects = getBoardSelectionObjects(instance.getActiveObject()).filter(isBoardTextObject);
    if (selectedTextObjects.length === 0) return;

    selectedTextObjects.forEach((object) => {
      object.set('fontSize', nextSize);
      if (typeof object.initDimensions === 'function') {
        object.initDimensions();
      }
      object.setCoords();
      sendBoardAction({ type: 'fabric-path', object: getBoardObjectJson(object) });
    });
    instance.requestRenderAll();

    const snapshot = instance.toJSON(BOARD_OBJECT_JSON_PROPS);
    snapshot.background = instance.backgroundColor || '#ffffff';
    sendBoardAction({ type: 'snapshot', snapshot });
  };

  const clampBoardZoom = (zoom) => Math.min(Math.max(zoom, BOARD_MIN_ZOOM), BOARD_MAX_ZOOM);

  const normalizeBoardUsers = (users) => Array.from(new Set(
    (Array.isArray(users) ? users : [])
      .filter((name) => typeof name === 'string' && name.trim() && !name.startsWith('__observer__'))
      .map((name) => name.trim())
  ));

  const resetBoardNavigationState = () => {
    boardNavigationRef.current = {
      mode: null,
      lastClientX: null,
      lastClientY: null,
      lastDistance: 0
    };
    isPanningRef.current = false;
  };

  const syncBoardZoom = (instance) => {
    if (!instance) return;
    const nextZoom = Math.round(clampBoardZoom(instance.getZoom()) * 100) / 100;
    if (boardZoomRef.current !== nextZoom) {
      boardZoomRef.current = nextZoom;
      setBoardZoom(nextZoom);
    }
  };

  const setBoardInteractionMode = (instance) => {
    if (!instance) return;
    instance.isDrawingMode = !boardPanModeRef.current && !boardSelectModeRef.current;
    instance.selection = boardSelectModeRef.current;
    instance.forEachObject((object) => {
      applyBoardObjectInteractivity(object, boardSelectModeRef.current);
    });
    if (!boardSelectModeRef.current && instance.getActiveObject()) {
      instance.discardActiveObject();
    }
    instance.requestRenderAll();
  };

  const panBoardViewport = (instance, dx, dy) => {
    if (!instance || (!dx && !dy)) return;
    const vpt = instance.viewportTransform ? [...instance.viewportTransform] : [1, 0, 0, 1, 0, 0];
    vpt[4] += dx;
    vpt[5] += dy;
    instance.setViewportTransform(vpt);
    instance.requestRenderAll();
  };

  const zoomBoardToPoint = (instance, nextZoom, point) => {
    if (!instance || !point) return;
    instance.zoomToPoint(point, clampBoardZoom(nextZoom));
    syncBoardZoom(instance);
    instance.requestRenderAll();
  };

  const zoomBoardByFactor = (factor) => {
    const instance = fabricInstanceRef.current;
    if (!instance) return;
    const center = new fabric.Point(instance.getWidth() / 2, instance.getHeight() / 2);
    zoomBoardToPoint(instance, instance.getZoom() * factor, center);
  };

  const getEventClientPoint = (event) => {
    if (!event) return null;
    if (event.touches?.length) return { x: event.touches[0].clientX, y: event.touches[0].clientY };
    if (event.changedTouches?.length) return { x: event.changedTouches[0].clientX, y: event.changedTouches[0].clientY };
    if (typeof event.clientX === 'number' && typeof event.clientY === 'number') {
      return { x: event.clientX, y: event.clientY };
    }
    return null;
  };

  const getTouchDistance = (touches) => {
    if (!touches || touches.length < 2) return 0;
    return Math.hypot(touches[1].clientX - touches[0].clientX, touches[1].clientY - touches[0].clientY);
  };

  const getTouchCenter = (touches) => {
    if (!touches || touches.length < 2) return null;
    return {
      x: (touches[0].clientX + touches[1].clientX) / 2,
      y: (touches[0].clientY + touches[1].clientY) / 2
    };
  };

  const isStylusTouch = (touch) => touch?.touchType === 'stylus';

  const disposeBoardCanvas = () => {
    const instance = fabricInstanceRef.current;
    if (!instance) return;
    if (typeof instance.__boardTouchCleanup === 'function') {
      instance.__boardTouchCleanup();
    }
    instance.dispose();
    fabricInstanceRef.current = null;
    boardZoomRef.current = 1;
    setBoardZoom(1);
    resetBoardNavigationState();
  };

  const clearBoardLocal = () => {
    if (!fabricInstanceRef.current) return;
    fabricInstanceRef.current.clear();
    fabricInstanceRef.current.backgroundColor = '#ffffff';
    fabricInstanceRef.current.renderAll();
  };

  const resetBoardView = () => {
    const instance = fabricInstanceRef.current;
    if (!instance) return;
    instance.setViewportTransform([1, 0, 0, 1, 0, 0]);
    instance.setZoom(1);
    syncBoardZoom(instance);
    instance.renderAll();
  };

  const initBoardCanvas = async () => {
    const canvasEl = boardCanvasRef.current;
    const container = boardContainerRef.current;
    if (!canvasEl || !container) return;

    if (fabricInstanceRef.current) {
      disposeBoardCanvas();
    }

    const instance = new fabric.Canvas(canvasEl, {
      isDrawingMode: !boardPanMode,
      selection: false,
      backgroundColor: '#ffffff'
    });
    const { width, height } = container.getBoundingClientRect();
    const targetHeight = Math.max(height, window.innerWidth < 768 ? 420 : 560);
    instance.setWidth(width);
    instance.setHeight(targetHeight);
    canvasEl.oncontextmenu = (event) => event.preventDefault();
    instance.upperCanvasEl.oncontextmenu = (event) => event.preventDefault();
    instance.upperCanvasEl.style.touchAction = 'none';
    if (instance.freeDrawingBrush) {
      instance.freeDrawingBrush.color = boardEraser ? '#ffffff' : boardColor;
      instance.freeDrawingBrush.width = boardEraser ? boardEraserWidth : boardWidth;
    }
    boardZoomRef.current = 1;
    setBoardZoom(1);
    resetBoardNavigationState();

    const sendObjectUpdate = (target) => {
      if (!target) return;
      const json = getBoardObjectJson(target);
      sendBoardAction({ type: 'fabric-path', object: json });
      const snapshot = instance.toJSON(BOARD_OBJECT_JSON_PROPS);
      snapshot.background = instance.backgroundColor || '#ffffff';
      sendBoardAction({ type: 'snapshot', snapshot });
    };

    instance.on('mouse:down', (opt) => {
      const event = opt.e;
      if (event?.type?.startsWith('touch')) return;
      const shouldPan = boardPanModeRef.current || event.button === 2 || event.altKey || event.metaKey;
      if (shouldPan) {
        isPanningRef.current = true;
        instance.isDrawingMode = false;
        const point = getEventClientPoint(event);
        boardNavigationRef.current = {
          mode: 'mouse-pan',
          lastClientX: point?.x ?? null,
          lastClientY: point?.y ?? null,
          lastDistance: 0
        };
      } else {
        instance.isDrawingMode = !boardSelectModeRef.current;
      }
    });

    instance.on('mouse:move', (opt) => {
      if (opt.e?.type?.startsWith('touch') || !isPanningRef.current) return;
      const event = opt.e;
      const point = getEventClientPoint(event);
      let dx = event.movementX || 0;
      let dy = event.movementY || 0;
      if ((!dx && !dy) && point && boardNavigationRef.current.lastClientX !== null) {
        dx = point.x - boardNavigationRef.current.lastClientX;
        dy = point.y - boardNavigationRef.current.lastClientY;
      }
      if (point) {
        boardNavigationRef.current.lastClientX = point.x;
        boardNavigationRef.current.lastClientY = point.y;
      }
      panBoardViewport(instance, dx, dy);
    });

    instance.on('mouse:up', (opt) => {
      if (opt?.e?.type?.startsWith('touch')) return;
      setBoardInteractionMode(instance);
      resetBoardNavigationState();
    });

    instance.on('mouse:wheel', (opt) => {
      const delta = opt.e.deltaY;
      let zoom = instance.getZoom();
      zoom *= 0.999 ** delta;
      const point = new fabric.Point(opt.e.offsetX, opt.e.offsetY);
      zoomBoardToPoint(instance, zoom, point);
      opt.e.preventDefault();
      opt.e.stopPropagation();
    });

    instance.on('object:modified', (event) => {
      if (!boardSelectModeRef.current) return;
      sendObjectUpdate(event.target);
    });

    instance.on('object:moving', (event) => {
      if (!boardSelectModeRef.current) return;
      sendObjectUpdate(event.target);
    });

    instance.on('text:changed', (event) => {
      sendObjectUpdate(event.target);
    });

    const upperCanvasEl = instance.upperCanvasEl;
    const handleTouchStart = (event) => {
      const touches = Array.from(event.touches || []);
      if (touches.length === 2) {
        const center = getTouchCenter(touches);
        boardNavigationRef.current = {
          mode: 'pinch',
          lastClientX: center?.x ?? null,
          lastClientY: center?.y ?? null,
          lastDistance: getTouchDistance(touches)
        };
        instance.isDrawingMode = false;
        event.preventDefault();
        return;
      }

      if (touches.length === 1 && boardPanModeRef.current && !isStylusTouch(touches[0])) {
        boardNavigationRef.current = {
          mode: 'touch-pan',
          lastClientX: touches[0].clientX,
          lastClientY: touches[0].clientY,
          lastDistance: 0
        };
        instance.isDrawingMode = false;
        event.preventDefault();
      }
    };

    const handleTouchMove = (event) => {
      const touches = Array.from(event.touches || []);
      if (touches.length === 2) {
        const center = getTouchCenter(touches);
        const distance = getTouchDistance(touches);
        if (!center || !distance) return;

        if (boardNavigationRef.current.mode !== 'pinch') {
          boardNavigationRef.current = {
            mode: 'pinch',
            lastClientX: center.x,
            lastClientY: center.y,
            lastDistance: distance
          };
        }

        const dx = center.x - (boardNavigationRef.current.lastClientX ?? center.x);
        const dy = center.y - (boardNavigationRef.current.lastClientY ?? center.y);
        panBoardViewport(instance, dx, dy);

        const previousDistance = boardNavigationRef.current.lastDistance || distance;
        const rect = upperCanvasEl.getBoundingClientRect();
        const point = new fabric.Point(center.x - rect.left, center.y - rect.top);
        zoomBoardToPoint(instance, instance.getZoom() * (distance / previousDistance), point);

        boardNavigationRef.current = {
          mode: 'pinch',
          lastClientX: center.x,
          lastClientY: center.y,
          lastDistance: distance
        };
        event.preventDefault();
        return;
      }

      if (touches.length === 1 && boardNavigationRef.current.mode === 'gesture-hold') {
        event.preventDefault();
        return;
      }

      if (touches.length === 1 && boardPanModeRef.current && boardNavigationRef.current.mode === 'touch-pan' && !isStylusTouch(touches[0])) {
        const touch = touches[0];
        const dx = touch.clientX - (boardNavigationRef.current.lastClientX ?? touch.clientX);
        const dy = touch.clientY - (boardNavigationRef.current.lastClientY ?? touch.clientY);
        boardNavigationRef.current.lastClientX = touch.clientX;
        boardNavigationRef.current.lastClientY = touch.clientY;
        panBoardViewport(instance, dx, dy);
        event.preventDefault();
      }
    };

    const handleTouchEnd = (event) => {
      const touches = Array.from(event.touches || []);
      if (touches.length === 2) {
        const center = getTouchCenter(touches);
        boardNavigationRef.current = {
          mode: 'pinch',
          lastClientX: center?.x ?? null,
          lastClientY: center?.y ?? null,
          lastDistance: getTouchDistance(touches)
        };
        event.preventDefault();
        return;
      }

      if (touches.length === 1 && boardPanModeRef.current && !isStylusTouch(touches[0])) {
        boardNavigationRef.current = {
          mode: 'touch-pan',
          lastClientX: touches[0].clientX,
          lastClientY: touches[0].clientY,
          lastDistance: 0
        };
        instance.isDrawingMode = false;
        event.preventDefault();
        return;
      }

      if (touches.length === 1 && boardNavigationRef.current.mode === 'pinch') {
        boardNavigationRef.current = {
          mode: 'gesture-hold',
          lastClientX: touches[0].clientX,
          lastClientY: touches[0].clientY,
          lastDistance: 0
        };
        instance.isDrawingMode = false;
        event.preventDefault();
        return;
      }

      setBoardInteractionMode(instance);
      resetBoardNavigationState();
    };

    upperCanvasEl.addEventListener('touchstart', handleTouchStart, { passive: false });
    upperCanvasEl.addEventListener('touchmove', handleTouchMove, { passive: false });
    upperCanvasEl.addEventListener('touchend', handleTouchEnd, { passive: false });
    upperCanvasEl.addEventListener('touchcancel', handleTouchEnd, { passive: false });
    instance.__boardTouchCleanup = () => {
      upperCanvasEl.removeEventListener('touchstart', handleTouchStart);
      upperCanvasEl.removeEventListener('touchmove', handleTouchMove);
      upperCanvasEl.removeEventListener('touchend', handleTouchEnd);
      upperCanvasEl.removeEventListener('touchcancel', handleTouchEnd);
    };

    instance.on('path:created', (event) => {
      if (boardSelectModeRef.current) {
        instance.remove(event.path);
        return;
      }
      const object = event.path;
      if (!object) return;
      object.selectable = false;
      const payload = {
        type: 'fabric-path',
        object: object.toJSON(['path', 'stroke', 'strokeWidth', 'fill', 'strokeLineCap', 'strokeLineJoin'])
      };
      sendBoardAction(payload);
      const snapshot = instance.toJSON(BOARD_OBJECT_JSON_PROPS);
      snapshot.background = instance.backgroundColor || '#ffffff';
      sendBoardAction({ type: 'snapshot', snapshot });
    });

    fabricInstanceRef.current = instance;
    setBoardInteractionMode(instance);
    instance.renderAll();
  };

  const handleRemoteFabricObject = async (data) => {
    if (!fabricInstanceRef.current) {
      await initBoardCanvas();
    }
    if (!fabricInstanceRef.current) return;

    if (data.type === 'clear') {
      clearBoardLocal();
      return;
    }

    if (data.type === 'snapshot' && data.snapshot) {
      fabricInstanceRef.current.loadFromJSON(data.snapshot, () => {
        fabricInstanceRef.current.forEachObject((object) => {
          applyBoardObjectInteractivity(object, boardSelectModeRef.current);
        });
        if (data.snapshot.background) {
          fabricInstanceRef.current.backgroundColor = data.snapshot.background;
        }
        fabricInstanceRef.current.renderAll();
      });
      return;
    }

    if (data.object && data.object.type === 'image' && data.object.src) {
      const instance = fabricInstanceRef.current;
      fabric.Image.fromURL(
        data.object.src,
        (image) => {
          image.set({
            left: data.object.left || 0,
            top: data.object.top || 0,
            scaleX: data.object.scaleX || 1,
            scaleY: data.object.scaleY || 1,
            angle: data.object.angle || 0,
            selectable: false,
            evented: false
          });
          applyBoardObjectInteractivity(image, boardSelectModeRef.current);
          instance.add(image);
          instance.renderAll();
        },
        { crossOrigin: 'anonymous' }
      );
      return;
    }

    if (data.object) {
      fabric.util.enlivenObjects([data.object], (objects) => {
        objects.forEach((object) => {
          applyBoardObjectInteractivity(object, boardSelectModeRef.current);
          fabricInstanceRef.current.add(object);
        });
        fabricInstanceRef.current.renderAll();
      });
    }
  };

  const addImageToBoard = (dataUrl) => {
    const instance = fabricInstanceRef.current;
    if (!instance) return;
    fabric.Image.fromURL(dataUrl, (image) => {
      const canvasWidth = instance.getWidth();
      const canvasHeight = instance.getHeight();
      const scale = Math.min((canvasWidth * 0.7) / image.width, (canvasHeight * 0.7) / image.height, 1);
      image.set({
        left: canvasWidth * 0.15,
        top: canvasHeight * 0.15,
        scaleX: scale,
        scaleY: scale,
        selectable: true,
        evented: true
      });
      instance.add(image);
      instance.setActiveObject(image);
      instance.renderAll();
      const imagePayload = getBoardObjectJson(image);
      sendBoardAction({ type: 'fabric-path', object: imagePayload });
      const snapshot = instance.toJSON(BOARD_OBJECT_JSON_PROPS);
      snapshot.background = instance.backgroundColor || '#ffffff';
      sendBoardAction({ type: 'snapshot', snapshot });
    });
  };

  const addTextToBoard = () => {
    const instance = fabricInstanceRef.current;
    if (!instance) return;

    const canvasWidth = instance.getWidth();
    const canvasHeight = instance.getHeight();
    const text = new fabric.IText('Escribí texto', {
      left: canvasWidth * 0.18,
      top: canvasHeight * 0.18,
      width: Math.min(360, canvasWidth * 0.62),
      fill: boardColor,
      fontSize: boardTextSize,
      fontFamily: 'Inter, Manrope, Arial, sans-serif',
      fontWeight: '600',
      editable: true,
      selectable: true,
      evented: true
    });

    boardPanModeRef.current = false;
    boardSelectModeRef.current = true;
    setBoardEraser(false);
    setBoardPanMode(false);
    setBoardSelectMode(true);
    instance.isDrawingMode = false;
    instance.selection = true;
    instance.add(text);
    instance.setActiveObject(text);
    instance.requestRenderAll();

    sendBoardAction({ type: 'fabric-path', object: getBoardObjectJson(text) });
    const snapshot = instance.toJSON(BOARD_OBJECT_JSON_PROPS);
    snapshot.background = instance.backgroundColor || '#ffffff';
    sendBoardAction({ type: 'snapshot', snapshot });

    window.setTimeout(() => {
      text.enterEditing();
      text.selectAll();
      text.hiddenTextarea?.focus();
    }, 0);
  };

  const exportBoardImage = async (format = 'png') => {
    if (!fabricInstanceRef.current) await initBoardCanvas();
    if (!fabricInstanceRef.current) return;
    setBoardExporting(true);
    try {
      const width = fabricInstanceRef.current.getWidth();
      const height = fabricInstanceRef.current.getHeight();
      const dataUrl = fabricInstanceRef.current.toDataURL({ format: 'png', multiplier: 2 });
      if (format === 'png') {
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = `pizarra-docente-${aulaId || 'aula'}.png`;
        link.click();
      } else {
        const pdf = new jsPDF({
          orientation: width >= height ? 'l' : 'p',
          unit: 'px',
          format: [width, height]
        });
        pdf.addImage(dataUrl, 'PNG', 0, 0, width, height);
        pdf.save(`pizarra-docente-${aulaId || 'aula'}.pdf`);
      }
    } catch (err) {
      setResourceError('No pudimos exportar la pizarra.');
    } finally {
      setBoardExporting(false);
    }
  };

  const startBoardConnection = async () => {
    if (!aulaId || !canUseBoard || boardConnectionRef.current) return;

    setBoardConnecting(true);
    setResourceError('');
    try {
      const connection = new HubConnectionBuilder()
        .withUrl(`${getHubBaseUrl()}/hubs/aula-docente-board`, {
          withCredentials: true,
          accessTokenFactory: () => apiService.getTokenFromCookies() || ''
        })
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Warning)
        .build();

      connection.on('UserJoinedBoard', (name) => {
        if (typeof name === 'string' && name.startsWith('__observer__')) return;
        setBoardUsers((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          return list.includes(name) ? list : [...list, name];
        });
        if (!boardStartedBy) {
          setBoardStartedBy(name);
          setBoardStatus(`${name} inició una pizarra colaborativa a las ${formatDateTime(new Date().toISOString())}`);
        }
        if (name !== userLabel && fabricInstanceRef.current) {
          const snapshot = fabricInstanceRef.current.toJSON(BOARD_OBJECT_JSON_PROPS);
          snapshot.background = fabricInstanceRef.current.backgroundColor || '#ffffff';
          sendBoardAction({ type: 'snapshot', snapshot });
        }
      });
      connection.on('UserLeftBoard', (name) => {
        if (typeof name === 'string' && name.startsWith('__observer__')) return;
        setBoardUsers((prev) => (Array.isArray(prev) ? prev.filter((item) => item !== name) : []));
      });
      connection.on('ConnectedUsersBoard', (users) => {
        const cleaned = normalizeBoardUsers(users);
        setBoardUsers(cleaned);
        if (cleaned.length > 0 && !boardStartedBy) {
          setBoardStartedBy(cleaned[0]);
          setBoardStatus(`${cleaned[0]} inició una pizarra colaborativa a las ${formatDateTime(new Date().toISOString())}`);
        }
        if (cleaned.length === 0) {
          setBoardStartedBy('');
          setBoardStatus('Nadie inició la pizarra aún.');
        }
      });
      connection.on('BoardSessionStarted', (payload) => {
        const users = normalizeRealtimeUsers(payload?.connectedUsers);
        const startedBy = payload?.startedBy || users[0] || userLabel;
        setBoardUsers(users.length > 0 ? users : [startedBy]);
        setBoardStartedBy(startedBy);
        setBoardStatus(`${startedBy} inició una pizarra colaborativa (en curso).`);
      });
      connection.on('BoardSessionEnded', () => {
        setBoardUsers([]);
        setBoardStartedBy('');
        setBoardStatus('Nadie inició la pizarra aún.');
      });
      connection.on('BoardAction', (payload) => {
        try {
          const data = JSON.parse(payload?.payload || '{}');
          if (payload?.autor !== 'system' && data.sourceId === sessionIdRef.current) return;
          if (payload?.autor === userLabel) return;
          if (data.type === 'snapshot-request') {
            if (fabricInstanceRef.current) {
              const snapshot = fabricInstanceRef.current.toJSON(BOARD_OBJECT_JSON_PROPS);
              snapshot.background = fabricInstanceRef.current.backgroundColor || '#ffffff';
              sendBoardAction({ type: 'snapshot', snapshot });
            }
            return;
          }
          void handleRemoteFabricObject(data);
        } catch (_) {}
      });
      connection.onreconnected(() => {
        connection.invoke('JoinBoard', aulaId, userLabel).catch(() => {});
      });

      await connection.start();
      await connection.invoke('JoinBoard', aulaId, userLabel);
      myBoardNameRef.current = userLabel;
      boardConnectionRef.current = connection;
      setBoardOpen(true);
      setBoardStatus('Conectado a la pizarra colaborativa');
      await registerTelemetry('aula_docente.board_joined', {
        origen: 'pizarra_aula_docente',
        displayName: userLabel
      });
      if (boardUsers.length === 0) {
        await registerTelemetry('aula_docente.board_started', {
          origen: 'pizarra_aula_docente',
          displayName: userLabel
        });
      }
      try {
        const users = await connection.invoke('GetConnectedUsers', aulaId);
        const cleaned = normalizeBoardUsers(users);
        setBoardUsers(cleaned);
      } catch (_) {}
      setTimeout(() => {
        void initBoardCanvas();
        void sendBoardAction({ type: 'snapshot-request' });
      }, 0);
    } catch (err) {
      setResourceError(err.message || 'No pudimos conectar la pizarra docente.');
    } finally {
      setBoardConnecting(false);
    }
  };

  const leaveBoard = async () => {
    if (boardConnectionRef.current) {
      try {
        await boardConnectionRef.current.invoke('LeaveBoard', aulaId);
      } catch (_) {}
      try {
        await boardConnectionRef.current.stop();
      } catch (_) {}
      boardConnectionRef.current = null;
    }

    setBoardOpen(false);
    setBoardUsers([]);
    setBoardStartedBy('');
    setBoardStatus('Nadie inició la pizarra aún.');
    disposeBoardCanvas();
  };

  const clearBoard = async () => {
    clearBoardLocal();
    await sendBoardAction({ type: 'clear' });
    await sendBoardAction({ type: 'snapshot', snapshot: { objects: [], background: '#ffffff' } });
  };

  useEffect(() => {
    boardPanModeRef.current = boardPanMode;
    boardSelectModeRef.current = boardSelectMode;
    const instance = fabricInstanceRef.current;
    if (!instance) return;
    if (instance.freeDrawingBrush) {
      instance.freeDrawingBrush.color = boardEraser ? '#ffffff' : boardColor;
      instance.freeDrawingBrush.width = boardEraser ? boardEraserWidth : boardWidth;
    }
    setBoardInteractionMode(instance);
  }, [boardColor, boardWidth, boardEraserWidth, boardEraser, boardPanMode, boardSelectMode]);

  useEffect(() => {
    if (!boardOpen) return;
    const timer = window.setTimeout(() => {
      void initBoardCanvas();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [boardOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const startCall = async () => {
    if (!aulaId || !canUseVideo) return;
    await setupCallHub();
    const connection = callConnectionRef.current;
    if (!connection) return;

    const room = `${jitsiBase}/aula-docente-${aulaId}-${Date.now()}`;
    try {
      await connection.invoke('StartCall', aulaId, userLabel, room);
      setCallInfo({
        startedBy: userLabel,
        roomUrl: room,
        startedAt: new Date().toISOString()
      });
      await registerTelemetry('aula_docente.call_started', {
        origen: 'videollamada_aula_docente',
        displayName: userLabel,
        ruta: room
      });
      await registerTelemetry('aula_docente.call_joined', {
        origen: 'videollamada_aula_docente',
        displayName: userLabel,
        ruta: room
      });
      window.open(room, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setResourceError(err.message || 'No pudimos iniciar la videollamada docente.');
    }
  };

  const joinCall = async () => {
    if (!canUseVideo || !callInfo?.roomUrl) return;
    await registerTelemetry('aula_docente.call_joined', {
      origen: 'videollamada_aula_docente',
      displayName: userLabel,
      ruta: callInfo.roomUrl
    });
    window.open(callInfo.roomUrl, '_blank', 'noopener,noreferrer');
  };

  const endCall = async () => {
    if (!callConnectionRef.current || !aulaId) return;
    try {
      await callConnectionRef.current.invoke('EndCall', aulaId);
      setCallInfo(null);
    } catch (err) {
      setResourceError(err.message || 'No pudimos finalizar la videollamada.');
    }
  };

  const handleOpenAssistant = async (assistant) => {
    const url = assistant.gptClasicoURLPublica || assistant.GPTClasicoURLPublica;
    if (!url) return;

    await registerTelemetry('aula_docente.assistant_opened', {
      assistantId: assistant.idOpenAIAssistant || assistant.IdOpenAIAssistant,
      assistantName: assistant.nombreVisible || assistant.nombre || assistant.Nombre || 'Asistente',
      assistantType: 'gpt_clasico',
      ruta: url
    });
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleOpenLink = async (link) => {
    const url = link.url || link.Url;
    if (!url) return;

    await registerTelemetry('aula_docente.link_opened', {
      ruta: url
    });
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleDownloadFile = async (file) => {
    const fileId = getId(file);
    const filename = file.nombreArchivo || file.NombreArchivo || 'archivo';
    if (!fileId) return;

    try {
      const blob = await aulaDocenteArchivosService.downloadFile(aulaId, fileId);
      downloadBlob(blob, filename);
      await registerTelemetry('aula_docente.file_downloaded', {
        fileId,
        fileName: filename,
        downloadType: 'single'
      });
    } catch (err) {
      setResourceError(err.message || 'No pudimos descargar el archivo.');
    }
  };

  const handleDownloadZip = async () => {
    try {
      const blob = await aulaDocenteArchivosService.downloadZip(aulaId);
      downloadBlob(blob, `archivos-${aulaNombre || 'aula-docente'}.zip`);
      await registerTelemetry('aula_docente.file_downloaded', {
        downloadType: 'zip'
      });
    } catch (err) {
      setResourceError(err.message || 'No pudimos descargar el ZIP.');
    }
  };

  const handleCreateMessage = async (event) => {
    event.preventDefault();
    if (!messageBody.trim()) return;

    setMessageSaving(true);
    setResourceError('');
    try {
      await aulaDocenteMensajesService.crearMensaje(aulaId, {
        Titulo: messageTitle.trim() || undefined,
        ContenidoHtml: buildMessageHtml(messageBody),
        Activo: true
      });
      await registerTelemetry('aula_docente.foro_thread_created', {
        ruta: 'foro'
      });
      setMessageTitle('');
      setMessageBody('');
      await loadResources();
    } catch (err) {
      setResourceError(err.message || 'No pudimos publicar el mensaje.');
    } finally {
      setMessageSaving(false);
    }
  };

  const handleStartReply = (message) => {
    setReplyParent(message);
    setReplyBody('');
  };

  const handleSubmitReply = async (event) => {
    event.preventDefault();
    if (!replyParent || !replyBody.trim()) return;

    const parentId = getId(replyParent);
    setReplySaving(true);
    setResourceError('');
    try {
      await aulaDocenteMensajesService.crearMensaje(aulaId, {
        IdMensajePadre: parentId,
        Titulo: `Re: ${replyParent.titulo || replyParent.Titulo || 'Mensaje'}`,
        ContenidoHtml: buildMessageHtml(replyBody),
        NombreRemitente: userLabel,
        Activo: true
      });
      await registerTelemetry('aula_docente.foro_reply_created', {
        ruta: 'foro',
        mensajePadreId: parentId
      });
      setReplyParent(null);
      setReplyBody('');
      setExpandedMessages((prev) => {
        const next = new Set(prev);
        next.add(parentId);
        return next;
      });
      await loadResources();
    } catch (err) {
      setResourceError(err.message || 'No pudimos enviar la respuesta.');
    } finally {
      setReplySaving(false);
    }
  };

  const renderMessageTree = (list, level = 0) => (
    (list || []).map((message, index) => {
      const messageId = getId(message);
      const respuestas = Array.isArray(message.respuestas || message.Respuestas)
        ? (message.respuestas || message.Respuestas)
        : [];
      const isExpanded = expandedMessages.has(messageId);

      return (
        <article
          key={messageId}
          className={`aula-message-item ${index % 2 === 0 ? 'alt' : ''}`}
          style={{ marginLeft: level * 12 }}
        >
          <div className="aula-message-header">
            <div className="aula-message-meta">
              <span className="aula-message-title">{message.titulo || message.Titulo || 'Mensaje'}</span>
              <span className="aula-message-author">
                {message.nombreRemitente || message.NombreRemitente || message.autorNombre || message.AutorNombre || 'Docente'}
              </span>
              <span className="aula-message-date">{formatDateTime(message.fechaAlta || message.FechaAlta)}</span>
            </div>
            <div className="message-actions">
              {respuestas.length > 0 && (
                <button
                  type="button"
                  className="reply-toggle"
                  onClick={() => {
                    setExpandedMessages((prev) => {
                      const next = new Set(prev);
                      if (next.has(messageId)) {
                        next.delete(messageId);
                      } else {
                        next.add(messageId);
                      }
                      return next;
                    });
                  }}
                >
                  <span className="badge">{respuestas.length}</span>
                  {isExpanded ? 'Ocultar respuestas' : 'Ver respuestas'}
                </button>
              )}
              <button type="button" className="reply-chip" onClick={() => handleStartReply(message)}>
                Responder
              </button>
            </div>
          </div>
          <div
            className="aula-message-body"
            dangerouslySetInnerHTML={{ __html: message.contenidoHtml || message.ContenidoHtml || '' }}
          />
          {respuestas.length > 0 && isExpanded && (
            <div className="aula-replies-block">
              {renderMessageTree(respuestas, level + 1)}
            </div>
          )}
        </article>
      );
    })
  );

  return (
    <div className="aula-docente-access-page">
      <header className="aula-docente-access-header">
        <button type="button" className="aula-docente-back-btn" onClick={() => navigate('/aulas-docentes')}>
          <i className="fas fa-arrow-left" />
          Aulas docentes
        </button>
        <div className="aula-docente-access-user">
          <i className="fas fa-user-tie" />
          <span>{userLabel}</span>
        </div>
      </header>

      {loading && (
        <main className="aula-docente-access-main">
          <div className="aula-docente-loading">
            <i className="fas fa-spinner fa-spin" />
            <span>Cargando aula docente...</span>
          </div>
        </main>
      )}

      {!loading && error && (
        <main className="aula-docente-access-main">
          <div className="aula-docente-error">
            <i className="fas fa-exclamation-triangle" />
            <p>{error}</p>
          </div>
        </main>
      )}

      {!loading && !error && aula && (
        <main className="aula-docente-access-main">
          <section className="aula-docente-access-intro">
            <div>
              <span className="aula-docente-access-kicker">Aula docente</span>
              <h1>{aulaNombre}</h1>
              {(aula.descripcion || aula.Descripcion) && <p>{aula.descripcion || aula.Descripcion}</p>}
            </div>
            <div className="aula-docente-access-facts">
              <span><i className="fas fa-id-badge" /> {aula.nicknameUrl || aula.NicknameUrl || 'Sin código'}</span>
              <span><i className="fas fa-user-shield" /> {aula.nombreCreador || aula.NombreCreador || 'Creador'}</span>
              <span><i className="fas fa-calendar" /> {aula.cicloLectivo || aula.CicloLectivo || 'Ciclo'}</span>
            </div>
          </section>

          {resourceError && (
            <div className="aula-docente-resource-alert">
              <i className="fas fa-exclamation-circle" />
              <span>{resourceError}</span>
            </div>
          )}

          <section className="aula-docente-access-grid">
            <div className="aula-docente-access-panel assistants">
              <div className="aula-docente-panel-header">
                <div>
                  <h2>Asistentes</h2>
                  <p>{asistentes.length} disponibles</p>
                </div>
                <i className="fas fa-robot" />
              </div>
              {!canUseAssistants ? (
                <div className="aula-docente-empty">Los asistentes no están visibles o no están habilitados para este colegio.</div>
              ) : asistentes.length === 0 ? (
                <div className="aula-docente-empty">No hay asistentes asignados.</div>
              ) : (
                <div className="aula-docente-assistant-list">
                  {asistentes.map((assistant) => (
                    <button
                      key={assistant.idOpenAIAssistant || assistant.IdOpenAIAssistant}
                      type="button"
                      className="aula-docente-assistant-card"
                      onClick={() => handleOpenAssistant(assistant)}
                    >
                      <span>
                        <strong>{assistant.nombreVisible || assistant.nombre || 'Asistente'}</strong>
                        <small>{assistant.nombreArea || assistant.nombreMateria || 'Asistente IA'}</small>
                      </span>
                      <i className="fas fa-external-link-alt" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {canUseChat && (
            <div className="aula-docente-access-panel live-chat">
              <div className="aula-docente-panel-header">
                <div>
                  <h2>Chat docente</h2>
                  <p className={(chatOpen || chatUsers.length > 0) ? 'aula-docente-live-status' : undefined}>
                    {chatOpen
                      ? `${chatUsers.length} conectados`
                      : chatUsers.length > 0
                        ? `${chatUsers.length} conectados. Chat en vivo iniciado.`
                        : 'Conversación en vivo'}
                  </p>
                </div>
                <button type="button" className="btn btn-primary" onClick={startChatConnection} disabled={chatConnecting || chatOpen}>
                  <i className={`fas ${chatConnecting ? 'fa-spinner fa-spin' : 'fa-comments'}`} />
                  {chatOpen ? 'Activo' : chatUsers.length > 0 ? 'Unirse' : 'Entrar'}
                </button>
              </div>
              {chatOpen ? (
                <div className="aula-docente-chat-layout">
                  <aside className="aula-docente-live-users">
                    <strong>Conectados</strong>
                    {chatUsers.length === 0 ? (
                      <span>Sin usuarios</span>
                    ) : chatUsers.map((name, index) => (
                      <span key={`${name}-${index}`}>{name === userLabel ? 'Yo' : name}</span>
                    ))}
                  </aside>
                  <div className="aula-docente-chat-window">
                    <div className="aula-docente-chat-messages" ref={chatScrollRef}>
                      {chatMessages.length === 0 ? (
                        <div className="aula-docente-empty compact">No hay mensajes en esta sesión.</div>
                      ) : chatMessages.map((message, index) => (
                        <div
                          key={`${message.autor}-${message.fechaUtc}-${index}`}
                          className={`aula-docente-chat-bubble ${message.autor === userLabel ? 'mine' : message.autor === 'sistema' ? 'system' : 'other'}`}
                        >
                          <div>
                            <strong>{message.autor === userLabel ? 'Yo' : message.autor}</strong>
                            <time>{new Date(message.fechaUtc || Date.now()).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}</time>
                          </div>
                          <p>{message.contenido}</p>
                        </div>
                      ))}
                    </div>
                    <div className="aula-docente-chat-compose">
                      <textarea
                        ref={chatInputRef}
                        value={chatInput}
                        onChange={handleChatInputChange}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' && !event.shiftKey) {
                            event.preventDefault();
                            void handleSendChatMessage();
                          }
                        }}
                        placeholder="Escribir en el chat docente"
                        rows={1}
                      />
                      <button type="button" className="btn btn-primary" onClick={handleSendChatMessage} disabled={!chatInput.trim()}>
                        Enviar
                      </button>
                    </div>
                    <div className="aula-docente-chat-actions-secondary">
                      <button type="button" className="btn btn-secondary" onClick={leaveChat}>
                        Salir
                      </button>
                      <button type="button" className="btn btn-secondary" onClick={downloadChatConversation} disabled={chatMessages.length === 0}>
                        Descargar conversación
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={`aula-docente-empty ${chatUsers.length > 0 ? 'aula-docente-live-notice' : ''}`}>
                  {chatUsers.length > 0
                    ? 'Hay un chat docente en vivo. Unite para conversar con los docentes conectados.'
                    : 'Abrí el chat para conversar con los docentes conectados.'}
                </div>
              )}
            </div>
            )}

            {canUseBoard && (
            <div className="aula-docente-access-panel board">
              <div className="aula-docente-panel-header">
                <div>
                  <h2>Pizarra colaborativa</h2>
                  <p className={(boardOpen || boardUsers.length > 0) ? 'aula-docente-live-status' : undefined}>{boardStatus}</p>
                </div>
                <button type="button" className="btn btn-primary" onClick={startBoardConnection} disabled={boardConnecting || boardOpen}>
                  <i className={`fas ${boardConnecting ? 'fa-spinner fa-spin' : 'fa-chalkboard'}`} />
                  {boardOpen ? 'En vivo' : boardUsers.length > 0 ? 'Unirse' : 'Iniciar'}
                </button>
              </div>
              {boardOpen ? (
                <div className="board-layout aula-docente-board-layout">
                  <div className="board-tools">
                    <label>Color</label>
                    <input type="color" value={boardColor} onChange={handleBoardColorChange} />
                    <label>Grosor</label>
                    <input
                      type="range"
                      min={1}
                      max={16}
                      value={boardWidth}
                      onChange={(event) => setBoardWidth(Number(event.target.value))}
                    />
                    <label>Tamaño texto</label>
                    <input
                      type="range"
                      min={12}
                      max={72}
                      value={boardTextSize}
                      onChange={handleBoardTextSizeChange}
                    />
                    {boardEraser && (
                      <>
                        <label>Tamaño goma</label>
                        <input
                          type="range"
                          min={4}
                          max={40}
                          value={boardEraserWidth}
                          onChange={(event) => setBoardEraserWidth(Number(event.target.value))}
                        />
                      </>
                    )}
                    <div className="board-mode-block">
                      <span className="board-mode-label">Modo de interacción</span>
                      <div className="board-toggle-row">
                        <button
                          type="button"
                          className={`btn-chip ${!boardEraser && !boardPanMode && !boardSelectMode ? 'active' : ''}`}
                          onClick={() => {
                            setBoardEraser(false);
                            setBoardPanMode(false);
                            setBoardSelectMode(false);
                          }}
                        >
                          Dibujar
                        </button>
                        <button
                          type="button"
                          className={`btn-chip ${boardEraser ? 'active' : ''}`}
                          onClick={() => {
                            setBoardEraser(true);
                            setBoardPanMode(false);
                            setBoardSelectMode(false);
                          }}
                        >
                          Borrar
                        </button>
                        <button
                          type="button"
                          className={`btn-chip ${boardPanMode ? 'active' : ''}`}
                          onClick={() => {
                            setBoardPanMode(true);
                            setBoardEraser(false);
                            setBoardSelectMode(false);
                          }}
                        >
                          Mover lienzo
                        </button>
                        <button
                          type="button"
                          className={`btn-chip ${boardSelectMode ? 'active' : ''}`}
                          onClick={() => {
                            setBoardSelectMode(true);
                            setBoardPanMode(false);
                            setBoardEraser(false);
                          }}
                        >
                          Editar objetos
                        </button>
                      </div>
                      <p className="board-mode-hint">
                        Dibujar: trazos libres. Borrar: pincel blanco. Mover: arrastrar lienzo. Editar objetos: mover o transformar imágenes/textos.
                      </p>
                    </div>
                    <div className="board-export">
                      <button type="button" className="btn btn-secondary" disabled={boardExporting} onClick={() => exportBoardImage('png')}>
                        {boardExporting ? 'Preparando...' : 'Exportar PNG'}
                      </button>
                      <button type="button" className="btn btn-secondary" disabled={boardExporting} onClick={() => exportBoardImage('pdf')}>
                        {boardExporting ? 'Preparando...' : 'Exportar PDF'}
                      </button>
                      <button type="button" className="btn btn-secondary" onClick={() => imageInputRef.current?.click()}>
                        Insertar imagen
                      </button>
                      <button type="button" className="btn btn-secondary" onClick={addTextToBoard}>
                        Insertar texto
                      </button>
                      <button type="button" className="btn btn-secondary" onClick={resetBoardView}>
                        Centrar
                      </button>
                      <button type="button" className="btn btn-secondary" onClick={clearBoard}>
                        Limpiar
                      </button>
                      <button type="button" className="btn btn-secondary" onClick={leaveBoard}>
                        Salir
                      </button>
                    </div>
                    <div className="board-users">
                      <h4>Conectados ({boardUsers.length})</h4>
                      <ul>
                        {boardUsers.length === 0 ? (
                          <li>Sin usuarios</li>
                        ) : boardUsers.map((name, index) => (
                          <li key={`${name}-${index}`} className={name === userLabel ? 'me' : ''}>
                            {name === userLabel ? 'Yo' : name}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="board-canvas" ref={boardContainerRef}>
                    <canvas ref={boardCanvasRef} />
                    <div className="board-floating-controls" role="group" aria-label="Controles de zoom de la pizarra docente">
                      <button type="button" className="board-floating-btn" aria-label="Acercar pizarra" onClick={() => zoomBoardByFactor(1.2)}>
                        +
                      </button>
                      <div className="board-zoom-indicator">{Math.round(boardZoom * 100)}%</div>
                      <button type="button" className="board-floating-btn" aria-label="Alejar pizarra" onClick={() => zoomBoardByFactor(1 / 1.2)}>
                        -
                      </button>
                    </div>
                    <div className="board-hint">
                      En tablet/iPad: pellizcá con dos dedos para zoom/desplazar y activá Mover para arrastrar con un dedo.
                    </div>
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onload = (readerEvent) => {
                          if (readerEvent.target?.result) {
                            addImageToBoard(readerEvent.target.result.toString());
                          }
                        };
                        reader.readAsDataURL(file);
                        event.target.value = '';
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div className={`aula-docente-empty ${boardUsers.length > 0 ? 'aula-docente-live-notice' : ''}`}>
                  {boardUsers.length > 0
                    ? 'Hay una pizarra docente en vivo. Unite para participar.'
                    : 'Iniciá la pizarra para trabajar en tiempo real con otros docentes.'}
                </div>
              )}
            </div>
            )}

            {canUseVideo && (
            <div className="aula-docente-access-panel video">
              <div className="aula-docente-panel-header">
                <div>
                  <h2>Videollamada</h2>
                  <p className={callInfo ? 'aula-docente-live-status' : undefined}>{callInfo ? `Sala abierta por ${callInfo.startedBy}` : 'Sala en vivo para docentes'}</p>
                </div>
                <i className={`fas ${callConnecting ? 'fa-spinner fa-spin' : 'fa-video'}`} />
              </div>
              {callInfo ? (
                <div className="aula-docente-video-banner live">
                  <div>
                    <strong>Videollamada activa</strong>
                    <span>Iniciada por {callInfo.startedBy} · {formatDateTime(callInfo.startedAt)}</span>
                  </div>
                  <div className="aula-docente-video-actions">
                    <button type="button" className="btn btn-primary" onClick={joinCall}>
                      Unirse
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={endCall}>
                      Finalizar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="aula-docente-video-start">
                  <p>Se abrirá una sala Jitsi y los demás docentes verán el aviso para unirse.</p>
                  <button type="button" className="btn btn-primary" onClick={startCall} disabled={callConnecting}>
                    <i className="fas fa-video" />
                    Iniciar videollamada
                  </button>
                </div>
              )}
            </div>
            )}

            {canUseFiles && (
            <div className="aula-docente-access-panel files">
              <div className="aula-docente-panel-header">
                <div>
                  <h2>Archivos</h2>
                  <p>{archivos.length} recursos</p>
                </div>
                <button type="button" className="btn btn-secondary" onClick={handleDownloadZip} disabled={!canUseFiles || archivos.length === 0}>
                  <i className="fas fa-file-archive" />
                  ZIP
                </button>
              </div>
              {resourcesLoading ? (
                <div className="aula-docente-empty"><i className="fas fa-spinner fa-spin" /> Cargando archivos...</div>
              ) : !canUseFiles ? (
                <div className="aula-docente-empty">Los archivos no están habilitados para este colegio.</div>
              ) : archivos.length === 0 ? (
                <div className="aula-docente-empty">No hay archivos cargados.</div>
              ) : (
                <div className="aula-docente-resource-list">
                  {archivos.map((file) => (
                    <div key={getId(file)} className="aula-docente-resource-row">
                      <span>
                        <strong>{file.nombreArchivo || file.NombreArchivo}</strong>
                        <small>{formatDateTime(file.fechaAlta || file.FechaAlta)}</small>
                      </span>
                      <button type="button" className="btn btn-secondary" onClick={() => handleDownloadFile(file)}>
                        <i className="fas fa-download" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            )}

            {canUseLinks && (
            <div className="aula-docente-access-panel links">
              <div className="aula-docente-panel-header">
                <div>
                  <h2>Links</h2>
                  <p>{links.length} vinculados</p>
                </div>
                <i className="fas fa-link" />
              </div>
              {resourcesLoading ? (
                <div className="aula-docente-empty"><i className="fas fa-spinner fa-spin" /> Cargando links...</div>
              ) : links.length === 0 ? (
                <div className="aula-docente-empty">No hay links cargados.</div>
              ) : (
                <div className="aula-docente-resource-list">
                  {links.map((link) => (
                    <button key={getId(link)} type="button" className="aula-docente-link-row" onClick={() => handleOpenLink(link)}>
                      <span>
                        <strong>{link.descripcion || link.Descripcion || 'Link'}</strong>
                        <small>{link.url || link.Url}</small>
                      </span>
                      <i className="fas fa-external-link-alt" />
                    </button>
                  ))}
                </div>
              )}
            </div>
            )}

            {canUseMessages && (
            <div className="aula-docente-access-panel messages">
              <div className="aula-docente-panel-header">
                <div>
                  <h2>Foro docente</h2>
                  <p>{mensajesTotal} mensajes publicados</p>
                </div>
                <i className="fas fa-comments" />
              </div>
              {!canUseMessages ? (
                <div className="aula-docente-empty">Los mensajes no están habilitados para este colegio.</div>
              ) : (
                <>
                  {canCreateForumThreads ? (
                    <form className="aula-docente-message-form" onSubmit={handleCreateMessage}>
                      <input
                        type="text"
                        value={messageTitle}
                        onChange={(event) => setMessageTitle(event.target.value)}
                        placeholder="Título del hilo"
                      />
                      <textarea
                        value={messageBody}
                        onChange={(event) => setMessageBody(event.target.value)}
                        placeholder="Crear hilo inicial para que los docentes respondan"
                        rows={4}
                      />
                      <button type="submit" className="btn btn-primary" disabled={messageSaving || !messageBody.trim()}>
                        <i className={`fas ${messageSaving ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`} />
                        Crear hilo
                      </button>
                    </form>
                  ) : (
                    <div className="aula-docente-empty compact">El dueño del aula crea los hilos iniciales. Podés responder dentro de cada conversación.</div>
                  )}
                  {resourcesLoading ? (
                    <div className="aula-docente-empty"><i className="fas fa-spinner fa-spin" /> Cargando mensajes...</div>
                  ) : mensajes.length === 0 ? (
                    <div className="aula-docente-empty">No hay mensajes publicados.</div>
                  ) : (
                    <div className="aula-messages-list">{renderMessageTree(mensajes)}</div>
                  )}
                  {replyParent && (
                    <form className="aula-reply-card" onSubmit={handleSubmitReply}>
                      <div className="aula-reply-header">
                        <div>
                          <h4>Responder a: {replyParent.titulo || replyParent.Titulo || 'Mensaje'}</h4>
                          <p className="aula-reply-preview">
                            {replyParent.nombreRemitente || replyParent.NombreRemitente || replyParent.autorNombre || replyParent.AutorNombre || 'Docente'}
                          </p>
                        </div>
                        <button type="button" className="btn btn-secondary" onClick={() => setReplyParent(null)}>
                          Cancelar
                        </button>
                      </div>
                      <div className="aula-reply-form">
                        <textarea
                          value={replyBody}
                          onChange={(event) => setReplyBody(event.target.value)}
                          placeholder="Escribir respuesta..."
                          rows={4}
                        />
                        <button type="submit" className="btn btn-primary" disabled={replySaving || !replyBody.trim()}>
                          <i className={`fas ${replySaving ? 'fa-spinner fa-spin' : 'fa-reply'}`} />
                          Enviar respuesta
                        </button>
                      </div>
                    </form>
                  )}
                </>
              )}
            </div>
            )}
          </section>
        </main>
      )}
    </div>
  );
};

export default AulaDocenteAccessPage;
